import {ClassName} from '#/utils/utils';
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ArrowUturnDownIcon,
  ArrowUturnUpIcon,
  MinusCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import {DisplayableTransactionType, TransactionType} from '@shareland/shareland-common';
import {FunctionComponent} from 'react';

export enum ContractEventNames {
  MARKET_TRANSACTION_RECEIPT = 'MarketTransaction',
  LIQUIDITY_PROVIDER_TOKEN_TRANSACTION_RECEIPT = 'LiquidityProviderTokenTransaction',
  COLLATERAL_POSITION_TRANSACTION_RECEIPT = 'CollateralPositionTransaction',
}

export type ActivityTileProps = {
  IconComponent: FunctionComponent<ClassName>;
  iconColorClassName: string;
  bgColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  dividerColor: string;
};

export type TransactionTypeConstant = {
  i18nKey:
    | 'buy'
    | 'sell'
    | 'borrowOpen'
    | 'borrowAdjust'
    | 'borrowClose'
    | 'rent'
    | 'stopRent'
    | 'liquidate'
    | 'limitOrder'
    | 'claim'
    | 'limitBuy'
    | 'limitSell';
};

export const transactionActivityTileProps: Record<DisplayableTransactionType, ActivityTileProps> = {
  [TransactionType.Purchase]: {
    IconComponent: PlusCircleIcon,
    iconColorClassName: 'stroke-transparent fill-transactionSellPositive',
    bgColor: 'bg-transactionSellPositiveLight',
    primaryTextColor: 'text-transactionSellPositive',
    secondaryTextColor: 'text-transactionSellPositive/70',
    dividerColor: 'border-transactionSellPositive/70',
  },
  [TransactionType.Sell]: {
    IconComponent: MinusCircleIcon,
    iconColorClassName: 'stroke-transparent fill-transactionSellNegative',
    bgColor: 'bg-transactionSellNegativeLight',
    primaryTextColor: 'text-transactionSellNegative',
    secondaryTextColor: 'text-transactionSellNegative/70',
    dividerColor: 'border-transactionSellNegative/70',
  },
  [TransactionType.OpenCollateralPosition]: {
    IconComponent: ArrowLeftOnRectangleIcon,
    iconColorClassName: 'stroke-transparent fill-transactionBorrowClose',
    bgColor: 'bg-transactionBorrowCloseLight',
    primaryTextColor: 'text-transactionBorrowClose',
    secondaryTextColor: 'text-transactionBorrowClose/70',
    dividerColor: 'border-transactionBorrowClose/70',
  },
  [TransactionType.AdjustCollateralPosition]: {
    IconComponent: ArrowLeftOnRectangleIcon,
    iconColorClassName: 'stroke-transparent fill-transactionBorrowClose',
    bgColor: 'bg-transactionBorrowCloseLight',
    primaryTextColor: 'text-transactionBorrowClose',
    secondaryTextColor: 'text-transactionBorrowClose/70',
    dividerColor: 'border-transactionBorrowClose/70',
  },
  [TransactionType.CloseCollateralPosition]: {
    IconComponent: ArrowRightOnRectangleIcon,
    iconColorClassName: 'stroke-transparent fill-transactionBorrowClose',
    bgColor: 'bg-transactionBorrowCloseLight',
    primaryTextColor: 'text-transactionBorrowClose',
    secondaryTextColor: 'text-transactionBorrowClose/70',
    dividerColor: 'border-transactionBorrowClose/70',
  },
  [TransactionType.MintLiquidityToken]: {
    IconComponent: ArrowUturnUpIcon,
    iconColorClassName: 'stroke-transparent fill-transactionRent',
    bgColor: 'bg-transactionRentLight',
    primaryTextColor: 'text-transactionRent',
    secondaryTextColor: 'text-transactionRent/70',
    dividerColor: 'border-transactionRent/70',
  },
  [TransactionType.BurnLiquidityToken]: {
    IconComponent: ArrowUturnDownIcon,
    iconColorClassName: 'stroke-transparent fill-transactionRent',
    bgColor: 'bg-transactionRentLight',
    primaryTextColor: 'text-transactionRent',
    secondaryTextColor: 'text-transactionRent/70',
    dividerColor: 'border-transactionRent/70',
  },
};

export const transactionTypeConstants: Record<TransactionType, TransactionTypeConstant> = {
  [TransactionType.Purchase]: {
    i18nKey: 'buy',
  },
  [TransactionType.Sell]: {
    i18nKey: 'sell',
  },
  [TransactionType.OpenCollateralPosition]: {
    i18nKey: 'borrowOpen',
  },
  [TransactionType.AdjustCollateralPosition]: {
    i18nKey: 'borrowAdjust',
  },
  [TransactionType.CloseCollateralPosition]: {
    i18nKey: 'borrowClose',
  },
  [TransactionType.MintLiquidityToken]: {
    i18nKey: 'rent',
  },
  [TransactionType.BurnLiquidityToken]: {
    i18nKey: 'stopRent',
  },
  [TransactionType.LiquidateCollateralPosition]: {
    i18nKey: 'liquidate',
  },
  [TransactionType.ExecuteCollateralPositionLimitOrder]: {
    i18nKey: 'limitOrder',
  },
  [TransactionType.ClaimRewards]: {
    i18nKey: 'claim',
  },
  [TransactionType.PurchaseLimitOrder]: {
    i18nKey: 'limitBuy',
  },
  [TransactionType.SellLimitOrder]: {
    i18nKey: 'limitSell',
  },
};
